import React, { useState, useEffect } from 'react';
import {
    Eye, Heart, HeartOff, MessageSquare, Phone, Mail, Bell,
    TrendingUp, Home, Calendar, Loader2, AlertCircle, Image,
    ChevronDown, ChevronUp, ExternalLink, X, Bot, Calculator,
    Search, SlidersHorizontal, MapPin, Clock
} from 'lucide-react';
import { getContactBuyerActivity } from '../../../api/contact';
import { describeSearch, describePreferences } from './activityDetail';
import './ContactActivityTimeline.css';

// ─── Event config: icon, label, colour ─────────────────────────────────────
const EVENT_CONFIG = {
    property_viewed:           { icon: Eye,            label: 'Viewed Property',        color: 'blue'  },
    property_view:             { icon: Eye,            label: 'Viewed Property',        color: 'blue'  },
    property_gallery_viewed:   { icon: Image,          label: 'Browsed Gallery',         color: 'indigo'},
    property_saved:            { icon: Heart,          label: 'Saved Property',          color: 'pink'  },
    property_unsaved:          { icon: HeartOff,       label: 'Unsaved Property',        color: 'gray'  },
    property_search:           { icon: Search,         label: 'Searched Properties',     color: 'blue'  },
    saved_search_created:      { icon: Search,         label: 'Saved a Search',          color: 'indigo'},
    suburb_viewed:             { icon: MapPin,         label: 'Viewed Suburb',           color: 'teal'  },
    preferences_updated:       { icon: SlidersHorizontal, label: 'Updated Preferences',  color: 'indigo'},
    buying_preferences_updated:{ icon: SlidersHorizontal, label: 'Updated Preferences',  color: 'indigo'},
    enquiry_sent:              { icon: MessageSquare,  label: 'Sent Enquiry',            color: 'emerald'},
    offer_submitted:           { icon: TrendingUp,     label: 'Submitted Offer',         color: 'purple'},
    call_clicked:              { icon: Phone,          label: 'Clicked Call',            color: 'green' },
    email_clicked:             { icon: Mail,           label: 'Clicked Email',           color: 'teal'  },
    contact_clicked:           { icon: Phone,          label: 'Clicked Contact',         color: 'green' },
    inspection_requested:      { icon: Calendar,       label: 'Requested Inspection',    color: 'orange'},
    signup_completed:          { icon: TrendingUp,     label: 'Signed Up to Portal',    color: 'purple'},
    alert_created:             { icon: Bell,           label: 'Created Property Alert',  color: 'yellow'},
    chatbot_session_started:   { icon: Bot,            label: 'Started GeeVee Chat',   color: 'cyan'  },
    chatbot_session_closed:    { icon: Bot,            label: 'Completed GeeVee Chat', color: 'cyan'  },
    chatbot_message_sent:      { icon: MessageSquare,  label: 'Sent Chat Message',     color: 'cyan'  },
    investment_calculator_opened:         { icon: Calculator, label: 'Opened Investment Calculator', color: 'blue'   },
    investment_calculator_mode_selected:  { icon: Calculator, label: 'Selected Calculator Mode',     color: 'indigo' },
    investment_calculator_calculated:     { icon: Calculator, label: 'Ran Investment Calculation',   color: 'emerald'},
};

const ENQUIRY_TYPE_LABELS = {
    GENERAL_ENQUIRY:    'General Enquiry',
    INSPECTION_REQUEST: 'Inspection Request',
    DOCUMENT_REQUEST:   'Document Request',
    PRICE_ENQUIRY:      'Price Enquiry',
    OTHER:              'Other',
};

// ─── Helpers ────────────────────────────────────────────────────────────────
function formatRelativeTime(dateStr) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1)   return 'Just now';
    if (diffMins < 60)  return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24)   return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7)   return `${diffDays}d ago`;
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatPrice(price) {
    if (!price) return null;
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(price);
}

function formatMethodOfSale(method) {
    if (!method) return null;
    return method.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ─── Stats bar ──────────────────────────────────────────────────────────────
// ─── Stats bar ──────────────────────────────────────────────────────────────
function StatsBar({ stats, activeType, onSelect }) {
    const items = [
        { id: 'total',    label: 'Total Events',      value: stats.total_events     || 0, clickable: false },
        { id: 'views',    label: 'Property Views',    value: stats.property_views   || 0, clickable: true },
        { id: 'enquiries', label: 'Enquiries',         value: stats.enquiries_sent   || 0, clickable: true },
        { id: 'offers',   label: 'Offers',            value: stats.offers_submitted || 0, clickable: true },
        { id: 'saved',    label: 'Saved',             value: stats.properties_saved || 0, clickable: true },
        { id: 'clicks',   label: 'Contact Clicks',    value: stats.intent_clicks    || 0, clickable: true },
    ];
    return (
        <div className="cat-stats-bar">
            {items.map(({ id, label, value, clickable }) => (
                <div 
                    key={label} 
                    className={`cat-stat ${clickable ? 'cat-stat-clickable' : ''} ${activeType === id ? 'cat-stat-active' : ''}`}
                    onClick={() => clickable && onSelect(id === activeType ? null : id)}
                >
                    <span className="cat-stat-value">{value}</span>
                    <span className="cat-stat-label">{label}</span>
                </div>
            ))}
        </div>
    );
}

// ─── Ranking View ───────────────────────────────────────────────────────────
function PropertyRankingView({ type, timeline, onClose }) {
    const typeLabel = {
        views: 'Property Views',
        enquiries: 'Enquiries',
        offers: 'Offers',
        saved: 'Saved Properties',
        clicks: 'Contact Clicks'
    }[type];

    const eventNames = {
        views: ['property_viewed'],
        enquiries: ['enquiry_sent'],
        offers: ['offer_submitted'],
        saved: ['property_saved'],
        clicks: ['call_clicked', 'email_clicked']
    }[type];

    // Group and rank
    const rankingMap = {};
    timeline.forEach(event => {
        if (eventNames.includes(event.event_name) && event.property) {
            const pid = event.property.id;
            if (!rankingMap[pid]) {
                rankingMap[pid] = {
                    property: event.property,
                    count: 0
                };
            }
            rankingMap[pid].count += 1;
        }
    });

    const ranked = Object.values(rankingMap).sort((a, b) => b.count - a.count);

    if (ranked.length === 0) return null;

    return (
        <div className="cat-ranking-view">
            <div className="cat-ranking-header">
                <h4 className="cat-ranking-title">
                    <TrendingUp size={16} />
                    Ranked by {typeLabel}
                </h4>
                <X size={18} className="cat-ranking-close" onClick={onClose} />
            </div>
            <div className="cat-ranking-list">
                {ranked.map((item, idx) => (
                    <div key={item.property.id} className="cat-ranking-item">
                        <div className="cat-ranking-number">{idx + 1}</div>
                        <div className="cat-ranking-property">
                            <div className="cat-ranking-prop-title">{item.property.display_address || item.property.title || item.property.address}</div>
                            <div className="cat-ranking-prop-sub">
                                {[item.property.suburb, item.property.state].filter(Boolean).join(', ')}
                            </div>
                        </div>
                        <div className="cat-ranking-count">{item.count} {item.count === 1 ? 'interaction' : 'interactions'}</div>
                        <a 
                            href={`/properties/${item.property.id}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="cat-property-link"
                        >
                            <ExternalLink size={14} />
                        </a>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─── Property card inline ────────────────────────────────────────────────────
function PropertyCard({ property }) {
    if (!property) return null;
    const address = property.display_address || property.title || property.address || 'Property Details';
    const suburb   = [property.suburb, property.state].filter(Boolean).join(', ');
    const price    = formatPrice(property.display_price);
    const saleType = formatMethodOfSale(property.method_of_sale);
    const beds     = property.beds;
    const baths    = property.baths;
    const propType = property.property_type;

    return (
        <div className="cat-property-card">
            <div className="cat-property-header">
                <Home size={14} className="cat-property-icon" />
                <span className="cat-property-address">{address}</span>
                {property.id && (
                    <a
                        href={`/properties/${property.id}`}
                        className="cat-property-link"
                        target="_blank"
                        rel="noopener noreferrer"
                        title="View Property"
                    >
                        <ExternalLink size={12} />
                    </a>
                )}
            </div>
            {suburb && <div className="cat-property-suburb">{suburb}</div>}
            <div className="cat-property-meta">
                {price && <span className="cat-badge cat-badge-price">{price}</span>}
                {saleType && <span className="cat-badge cat-badge-sale">{saleType}</span>}
                {propType && <span className="cat-badge cat-badge-type">{propType}</span>}
                {beds != null && beds > 0 && <span className="cat-badge cat-badge-feature">🛏 {beds}</span>}
                {baths != null && baths > 0 && <span className="cat-badge cat-badge-feature">🚿 {baths}</span>}
            </div>
        </div>
    );
}

// ─── Single event item ───────────────────────────────────────────────────────
function EventItem({ event }) {
    const cfg = EVENT_CONFIG[event.event_name] || {
        icon: TrendingUp,
        label: (event.event_name || 'activity').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        color: 'gray',
    };
    const Icon = cfg.icon;
    // A property-scoped event links straight through to that listing. Rows are
    // opened in a new tab (matching the inline property links) via onClick so we
    // don't nest an <a> inside the row's existing property links.
    const propertyId = event.property?.id;
    const openProperty = (e) => {
        if (!propertyId) return;
        if (e.target.closest('a, button')) return; // let inner links/buttons win
        window.open(`/properties/${propertyId}`, '_blank', 'noopener');
    };
    const onKeyActivate = (e) => {
        if (!propertyId) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            window.open(`/properties/${propertyId}`, '_blank', 'noopener');
        }
    };
    const enquiryType = event.properties_json?.enquiry_type;
    const calcProps = event.properties_json || {};
    const isCalcEvent = event.event_name === 'investment_calculator_calculated';
    const isAnyCalcEvent = event.event_name?.startsWith('investment_calculator_');
    // A calculator event with no linked listing is a standalone-tool use (opened from
    // the nav rather than "Analyse This Investment" on a property) — flag it so the
    // empty row reads as intentional, not as missing data.
    const isStandaloneCalc = isAnyCalcEvent && !event.property;
    const calcMode = calcProps.mode;
    // What the buyer entered vs. what the calculator computed (either may be merged
    // in from the same session's "calculated" event by the backend).
    const hasCalcInputs = isAnyCalcEvent && (
        calcProps.price != null || calcProps.weekly_rent != null ||
        calcProps.num_units != null || calcProps.deposit_pct != null ||
        calcProps.interest_rate_pct != null || !!calcProps.property_type
    );
    const hasCalcResults = isAnyCalcEvent && (
        calcProps.gross_yield_pct != null || calcProps.net_yield_pct != null ||
        calcProps.annual_cash_flow != null || calcProps.break_even_weekly_rent != null
    );
    const hasCalcData = hasCalcInputs || hasCalcResults;

    return (
        <div
            className={`cat-event cat-event--${cfg.color}${propertyId ? ' cat-event--clickable' : ''}`}
            onClick={propertyId ? openProperty : undefined}
            onKeyDown={propertyId ? onKeyActivate : undefined}
            role={propertyId ? 'link' : undefined}
            tabIndex={propertyId ? 0 : undefined}
            title={propertyId ? 'View property' : undefined}
        >
            <div className={`cat-event-icon cat-icon--${cfg.color}`}>
                <Icon size={15} />
            </div>
            <div className="cat-event-body">
                <div className="cat-event-header">
                    <span className="cat-event-label">{cfg.label}</span>
                    {enquiryType && (
                        <span className="cat-badge cat-badge-enquiry">
                            {ENQUIRY_TYPE_LABELS[enquiryType] || enquiryType}
                        </span>
                    )}
                    {calcMode && (
                        <span className="cat-badge cat-badge-type">{calcMode}</span>
                    )}
                    {isStandaloneCalc && (
                        <span
                            className="cat-badge"
                            style={{
                                background: 'var(--c-panel-warn)',
                                color: 'var(--c-on-warn)',
                                border: '1px solid #fcd9a8',
                                fontWeight: 700,
                                letterSpacing: '0.02em',
                                textTransform: 'uppercase',
                                fontSize: '10px',
                            }}
                            title="Opened directly from the nav, not from a specific listing"
                        >
                            Opened directly
                        </span>
                    )}
                    <span className="cat-event-time" title={event.occurred_at}>
                        {formatRelativeTime(event.occurred_at)}
                    </span>
                </div>
                {isAnyCalcEvent && hasCalcData && (
                    <div style={{ marginTop: 6 }}>
                        {/* Inputs the buyer entered */}
                        {hasCalcInputs && (
                            <div className="cat-property-meta" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                                <span style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Inputs</span>
                                {calcProps.price != null && (
                                    <span className="cat-badge cat-badge-price">{formatPrice(calcProps.price)}</span>
                                )}
                                {calcProps.weekly_rent != null && (
                                    <span className="cat-badge cat-badge-type">{formatPrice(calcProps.weekly_rent)}/wk rent</span>
                                )}
                                {calcProps.num_units != null && (
                                    <span className="cat-badge cat-badge-type">{calcProps.num_units} units</span>
                                )}
                                {calcProps.property_type && (
                                    <span className="cat-badge cat-badge-type">{calcProps.property_type}</span>
                                )}
                                {calcProps.deposit_pct != null && (
                                    <span className="cat-badge cat-badge-type">{calcProps.deposit_pct}% deposit</span>
                                )}
                                {calcProps.interest_rate_pct != null && (
                                    <span className="cat-badge cat-badge-type">{calcProps.interest_rate_pct}% rate</span>
                                )}
                            </div>
                        )}
                        {/* Computed results */}
                        {hasCalcResults && (
                            <div className="cat-property-meta" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 4 }}>
                                <span style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Result</span>
                                {calcProps.gross_yield_pct != null && (
                                    <span className="cat-badge cat-badge-feature">{Number(calcProps.gross_yield_pct).toFixed(2)}% gross</span>
                                )}
                                {calcProps.net_yield_pct != null && (
                                    <span className="cat-badge cat-badge-feature">{Number(calcProps.net_yield_pct).toFixed(2)}% net</span>
                                )}
                                {calcProps.annual_cash_flow != null && (
                                    <span className={`cat-badge ${calcProps.annual_cash_flow >= 0 ? 'cat-badge-sale' : 'cat-badge-enquiry'}`}>
                                        {formatPrice(calcProps.annual_cash_flow)}/yr cashflow
                                    </span>
                                )}
                                {calcProps.break_even_weekly_rent != null && (
                                    <span className="cat-badge cat-badge-type">{formatPrice(calcProps.break_even_weekly_rent)}/wk break-even</span>
                                )}
                            </div>
                        )}
                    </div>
                )}
                {event.event_name === 'property_search' && (() => {
                    const chips = describeSearch(event.properties_json || {});
                    return (
                        <div className="cat-property-meta" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 6 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Searched</span>
                            {chips.length
                                ? chips.map((c, i) => <span key={i} className="cat-badge cat-badge-type">{c}</span>)
                                : <span className="cat-badge cat-badge-feature">Browsed all listings</span>}
                        </div>
                    );
                })()}
                {(event.event_name === 'preferences_updated' || event.event_name === 'buying_preferences_updated') && (() => {
                    const chips = describePreferences(event.properties_json || {});
                    if (!chips.length) return null;
                    return (
                        <div className="cat-property-meta" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 6 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Preferences</span>
                            {chips.map((c, i) => <span key={i} className="cat-badge cat-badge-type">{c}</span>)}
                        </div>
                    );
                })()}
                <PropertyCard property={event.property} />
            </div>
        </div>
    );
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function ContactActivityTimeline({ contactId }) {
    const [data, setData]             = useState(null);
    const [loading, setLoading]       = useState(true);
    const [error, setError]           = useState(null);
    const [showAll, setShowAll]       = useState(false);
    const [activeDrillDown, setActiveDrillDown] = useState(null);

    useEffect(() => {
        if (!contactId) return;
        setLoading(true);
        setError(null);
        getContactBuyerActivity(contactId)
            .then(res => setData(res.data))
            .catch(err => {
                console.error('Error loading buyer activity:', err);
                setError('Failed to load activity data.');
            })
            .finally(() => setLoading(false));
    }, [contactId]);

    if (loading) {
        return (
            <div className="cat-state cat-state--loading">
                <Loader2 size={28} className="cat-spinner" />
                <p>Loading activity timeline…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="cat-state cat-state--error">
                <AlertCircle size={28} />
                <p>{error}</p>
            </div>
        );
    }

    // No portal account and no synthetic events at all → show true empty state
    if (!data || (data.no_portal_account && (!data.timeline || data.timeline.length === 0))) {
        return (
            <div className="cat-state cat-state--empty">
                <TrendingUp size={36} />
                <h4>No Portal Activity</h4>
                <p>This contact hasn't signed up to the Buyer Portal yet, so there's no engagement data to display.</p>
            </div>
        );
    }

    const { buyer, stats, timeline } = data;
    const noPortalAccount = data.no_portal_account || !buyer;
    // "Last active" prefers the most recent real interaction, falling back to the
    // last portal sign-in/seen. Read from either the buyer object or the top
    // level so we're resilient to where the API nests it.
    const lastActiveAt =
        data.last_activity_at || buyer?.last_activity_at ||
        buyer?.last_seen_at || data.last_seen_at || null;
    const PREVIEW_COUNT = 10;
    const visibleEvents = showAll ? timeline : timeline.slice(0, PREVIEW_COUNT);
    const hasMore = timeline.length > PREVIEW_COUNT;

    return (
        <div className="cat-root">
            {/* Buyer portal info banner — only shown for registered portal accounts */}
            {!noPortalAccount && buyer && (
                <div className="cat-buyer-banner">
                    <div className="cat-buyer-info">
                        <span className="cat-buyer-name">{buyer.name}</span>
                        <span className="cat-buyer-email">{buyer.email}</span>
                        {lastActiveAt && (
                            <span className="cat-buyer-lastactive" title={lastActiveAt}>
                                <Clock size={12} /> Last active {formatRelativeTime(lastActiveAt)}
                            </span>
                        )}
                    </div>
                    <span className="cat-badge cat-badge-portal">Buyer Portal Account</span>
                </div>
            )}
            {/* Notice for non-portal contacts with synthetic events */}
            {noPortalAccount && timeline.length > 0 && (
                <div className="cat-buyer-banner" style={{ background: 'var(--c-panel-ok)', borderColor: 'var(--c-line-ok)' }}>
                    <div className="cat-buyer-info">
                        <span className="cat-buyer-name" style={{ color: 'var(--c-on-ok)' }}>Enquiry / Offer Activity</span>
                        <span className="cat-buyer-email" style={{ color: '#4ade80' }}>No Buyer Portal account — showing submitted enquiries &amp; offers</span>
                    </div>
                </div>
            )}

            <StatsBar 
                stats={stats} 
                activeType={activeDrillDown} 
                onSelect={(type) => setActiveDrillDown(type)} 
            />

            {activeDrillDown && (
                <PropertyRankingView 
                    type={activeDrillDown} 
                    timeline={timeline} 
                    onClose={() => setActiveDrillDown(null)} 
                />
            )}

            {timeline.length === 0 ? (
                <div className="cat-state cat-state--empty">
                    <TrendingUp size={36} />
                    <h4>No interactions yet</h4>
                    <p>This buyer has signed up but hasn't interacted with any properties yet.</p>
                </div>
            ) : (
                <>
                    <div className="cat-timeline">
                        {visibleEvents.map(event => (
                            <EventItem key={event.event_id} event={event} />
                        ))}
                    </div>
                    {hasMore && (
                        <button
                            className="cat-show-more"
                            onClick={() => setShowAll(prev => !prev)}
                        >
                            {showAll
                                ? <><ChevronUp size={15} /> Show less</>
                                : <><ChevronDown size={15} /> Show all {timeline.length} events</>
                            }
                        </button>
                    )}
                </>
            )}
        </div>
    );
}

